import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/lib/SiteContent";

/**
 * SEO — premium per-page meta tag manager.
 *
 * Per-page <SEO> instances override global defaults configured by the admin
 * via /admin/settings → SEO section (settings.seo.*).
 *
 * Renders: <title>, meta description/keywords, canonical, Open Graph,
 * Twitter Card, robots, and JSON-LD (Organization + WebSite + WebPage).
 */
export const SEO = ({
  title,
  description,
  keywords,
  image,
  type = "website",
  robots,
  noIndex = false,
  jsonLd: pageJsonLd,
}) => {
  const settings = useSettings();
  const location = useLocation();
  const seo = settings?.seo || {};
  const brand = settings?.brand || {};
  const contact = settings?.contact || {};
  const social = settings?.social || {};

  const siteUrl = (seo.siteUrl || "https://www.seetaiproperties.com").replace(/\/$/, "");
  const siteTitle = seo.siteTitle || "Seetai Properties — Crafting Luxury. Building Legacy.";
  const titleTemplate = seo.titleTemplate || "%s | Seetai Properties";
  const fallbackDescription =
    seo.defaultDescription ||
    "Seetai Properties Pvt. Ltd. — luxury real estate developer behind Silva City, a 66-acre premium NA-plotted township in Khor, Bhandgaon.";
  const fallbackKeywords = seo.defaultKeywords || "";
  const locale = seo.locale || "en_IN";
  const twitterHandle = seo.twitterHandle || "@seetaiproperties";
  const ogImageRel = image || seo.ogImageUrl || "/og-image.jpg";
  const ogImageAbs = /^https?:\/\//i.test(ogImageRel) ? ogImageRel : `${siteUrl}${ogImageRel}`;

  const fullTitle = useMemo(() => {
    if (!title) return siteTitle;
    if (titleTemplate.includes("%s")) return titleTemplate.replace("%s", title);
    return `${title} | ${brand.name || "Seetai Properties"}`;
  }, [title, titleTemplate, siteTitle, brand.name]);

  const canonical = `${siteUrl}${location.pathname === "/" ? "" : location.pathname}`;
  const robotsContent = robots || (noIndex ? "noindex,nofollow" : "index,follow");
  const finalDescription = description || fallbackDescription;
  const finalKeywords = keywords || fallbackKeywords;

  // Organisation JSON-LD (rendered once site-wide via this component)
  const orgJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": ["Organization", "RealEstateAgent"],
      name: brand.name || "Seetai Properties",
      legalName: brand.legalName || "Seetai Properties Private Limited",
      url: siteUrl,
      logo: `${siteUrl}/brand/seetai-emblem-512.png`,
      image: ogImageAbs,
      description: fallbackDescription,
      telephone: contact.whatsapp || "+91 96372 41414",
      email: contact.email || "info@seetaiproperties.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: contact.siteAddress || "Airport Link Road, Jejuri Road, Bhandgaon",
        addressLocality: "Bhandgaon",
        addressRegion: "Maharashtra",
        postalCode: "412214",
        addressCountry: "IN",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 18.4125,
        longitude: 74.325306,
      },
      sameAs: [
        social.linkedin && social.linkedin !== "#" ? social.linkedin : null,
        social.facebook && social.facebook !== "#" ? social.facebook : null,
        social.instagram && social.instagram !== "#" ? social.instagram : null,
        social.youtube && social.youtube !== "#" ? social.youtube : null,
        social.twitter && social.twitter !== "#" ? social.twitter : null,
      ].filter(Boolean),
      foundingDate: brand.foundedYear || "2026",
      areaServed: { "@type": "State", name: "Maharashtra, India" },
    }),
    [brand, siteUrl, ogImageAbs, fallbackDescription, contact, social]
  );

  // Website JSON-LD with SearchAction (helps Google show search box in SERP)
  const websiteJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: siteUrl,
      name: brand.name || "Seetai Properties",
      description: fallbackDescription,
      publisher: { "@type": "Organization", name: brand.legalName || "Seetai Properties Private Limited" },
      inLanguage: "en-IN",
    }),
    [siteUrl, brand, fallbackDescription]
  );

  const webpageJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      url: canonical,
      name: fullTitle,
      description: finalDescription,
      isPartOf: { "@type": "WebSite", url: siteUrl, name: brand.name || "Seetai Properties" },
      inLanguage: "en-IN",
    }),
    [canonical, fullTitle, finalDescription, siteUrl, brand.name]
  );

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-IN" />
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={robotsContent} />
      <meta name="author" content={brand.legalName || "Seetai Properties Private Limited"} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageAbs} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={brand.name || "Seetai Properties"} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImageAbs} />
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
      {twitterHandle && <meta name="twitter:creator" content={twitterHandle} />}

      {/* Theme & misc */}
      <meta name="theme-color" content="#0a0a0a" />
      <meta name="format-detection" content="telephone=yes" />

      {/* JSON-LD: Organization (RealEstateAgent) */}
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageJsonLd)}</script>
      {pageJsonLd && (
        <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
